import { CaretRight, Plus, Ticket, Trash } from '@phosphor-icons/react';
import { personColors, type PersonColor } from '@shh/shared';
import { Avatar, Button, ChoiceGroup, Icon, Sheet, Surface, Text, TextField } from '@shh/ui';
import { useState } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { addMember, changeMemberKind, createInvite, updateMember } from '../../data/api';
import type { M } from '../../data/derive';
import { useFamily } from '../../data/family';
import {
  colorNames,
  kindToProfile,
  nextFreeColor,
  personKinds,
  profileToKind,
  type PersonKind,
} from '../../lib/people';
import { settingsText } from '../../i18n/settings';
import { locale } from '../../lib/locale';

/** Settings → Familie: people, colours, profiles, invites for teens and the second parent. */
export function FamilySettings() {
  const f = useFamily();
  const [editing, setEditing] = useState<M | 'new' | null>(null);
  return (
    <div className="flex flex-col gap-gap-md">
      <ul className="flex flex-col gap-gap-xs">
        {f.members.map((m) => (
          <li key={m.id}>
            <AriaButton
              onPress={() => setEditing(m)}
              className="flex w-full cursor-pointer items-center gap-gap-md rounded-control bg-surface-raised p-inset-xs text-left outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus data-hovered:bg-action-quiet-hover"
            >
              <Avatar name={m.name} color={m.color} emoji={m.emoji} size="md" decorative />
              <span className="flex flex-1 flex-col">
                <span className="font-bold">{m.name}</span>
                <span className="text-sm text-text-secondary">
                  {personKinds.find((k) => k.value === profileToKind(m.role, m.ageProfile))?.label}
                  {m.uid ? settingsText.ownAccount : ''}
                </span>
              </span>
              <Icon icon={CaretRight} size="sm" className="text-text-muted" />
            </AriaButton>
          </li>
        ))}
      </ul>
      <Button
        variant="secondary"
        icon={Plus}
        onPress={() => setEditing('new')}
        className="self-start"
      >
        {settingsText.addPerson}
      </Button>
      <Sheet
        isOpen={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        title={editing === 'new' ? settingsText.newPerson : (editing?.name ?? '')}
      >
        {editing && (
          <PersonForm
            key={editing === 'new' ? 'new' : editing.id}
            member={editing === 'new' ? undefined : editing}
            onDone={() => setEditing(null)}
          />
        )}
      </Sheet>
    </div>
  );
}

/** "14.11." / "14.11" ↔ "11-14" – day and month only, no year (1.13). */
export function parseBirthday(v: string): string | null | undefined {
  const t = v.trim();
  if (!t) return null;
  const m = /^(\d{1,2})[./](\d{1,2})\.?$/.exec(t);
  if (!m) return undefined;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const max = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
  if (!max || day < 1 || day > max) return undefined;
  return `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
const showBirthday = (b?: string) =>
  b
    ? locale === 'de'
      ? `${Number(b.slice(3))}.${Number(b.slice(0, 2))}.`
      : `${Number(b.slice(3))}/${Number(b.slice(0, 2))}`
    : '';

function PersonForm({ member, onDone }: { member?: M; onDone: () => void }) {
  const f = useFamily();
  const used = f.members.filter((m) => m.id !== member?.id).map((m) => m.color);
  const [name, setName] = useState(member?.name ?? '');
  const [emoji, setEmoji] = useState(member?.emoji ?? '');
  const [color, setColor] = useState<PersonColor>(member?.color ?? nextFreeColor(used));
  const [kind, setKind] = useState<PersonKind>(
    member ? profileToKind(member.role, member.ageProfile) : 'kid',
  );
  const [birthday, setBirthday] = useState(showBirthday(member?.birthday));
  const parsedBirthday = parseBirthday(birthday);
  const [invite, setInvite] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [confirmRemove, setConfirmRemove] = useState(false);
  // Kids grow up (kid ↔ teen, via the server – the role decides permissions); parents stay parents.
  const kinds = member
    ? personKinds.filter((k) => (k.value === 'parent') === (member.role === 'parent'))
    : personKinds;

  const save = async () => {
    setBusy(true);
    setError(undefined);
    try {
      if (member) {
        if (kind !== 'parent' && kind !== profileToKind(member.role, member.ageProfile)) {
          await changeMemberKind(member.id, kind);
        }
        await updateMember(f.householdId, member.id, {
          name: name.trim(),
          color,
          emoji: emoji.trim() || null,
          ageProfile: kindToProfile(kind).ageProfile,
          birthday: parsedBirthday ?? null,
        });
      } else {
        const { memberId } = await addMember({
          name: name.trim(),
          color,
          emoji: emoji.trim() || undefined,
          ...kindToProfile(kind),
          withRoutines: true,
        });
        if (parsedBirthday) {
          await updateMember(f.householdId, memberId, { birthday: parsedBirthday });
        }
      }
      onDone();
    } catch {
      setError(settingsText.failed);
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-gap-lg">
      <div className="flex items-end gap-gap-md">
        <Avatar name={name || '?'} color={color} emoji={emoji || undefined} size="lg" decorative />
        <TextField
          label={settingsText.name}
          size="lg"
          value={name}
          onChange={setName}
          className="flex-1"
          autoFocus={!member}
        />
      </div>
      <ChoiceGroup label={settingsText.whoIsThis} value={kind} onChange={setKind} choices={kinds} />
      <ChoiceGroup
        label={settingsText.color}
        layout="tile"
        value={color}
        onChange={setColor}
        choices={personColors.map((c) => ({
          value: c,
          label: colorNames[c],
          description: used.includes(c) ? settingsText.taken : undefined,
          picture: <Avatar name={name || '?'} color={c} size="sm" decorative />,
        }))}
      />
      <EmojiPicker value={emoji} onChange={setEmoji} />
      <TextField
        label={settingsText.birthday}
        description={settingsText.birthdayHint}
        placeholder={settingsText.birthdayPlaceholder}
        inputMode="numeric"
        value={birthday}
        onChange={setBirthday}
        isInvalid={parsedBirthday === undefined}
        errorMessage={settingsText.birthdayError}
      />
      {!member && kindToProfile(kind).role !== 'parent' && (
        <Text size="sm" tone="muted">
          {settingsText.routinesHint}
        </Text>
      )}

      {member && member.role !== 'kid' && !member.uid && (
        <Surface tone="sunken" className="flex flex-col gap-gap-sm">
          <Text weight="semibold">{settingsText.accountFor(member.name)}</Text>
          {invite ? (
            <p role="status">
              {settingsText.inviteCode}{' '}
              <strong className="font-display text-2xl tracking-widest">{invite}</strong>
              <span className="block text-sm text-text-secondary">
                {settingsText.inviteValid(member.name)}
              </span>
            </p>
          ) : (
            <Button
              variant="secondary"
              icon={Ticket}
              onPress={() => void createInvite(member.id).then((r) => setInvite(r.code))}
            >
              {settingsText.createInvite}
            </Button>
          )}
        </Surface>
      )}

      {error && (
        <p role="alert" className="font-semibold text-feedback-danger">
          {error}
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-gap-sm">
        {member && member.id !== f.meId ? (
          confirmRemove ? (
            <Button
              variant="danger"
              icon={Trash}
              onPress={() =>
                void updateMember(f.householdId, member.id, { archived: true }).then(onDone)
              }
            >
              {settingsText.reallyRemove}
            </Button>
          ) : (
            <Button variant="quiet" icon={Trash} onPress={() => setConfirmRemove(true)}>
              {settingsText.remove}
            </Button>
          )
        ) : (
          <span />
        )}
        <Button
          size="lg"
          isDisabled={!name.trim() || busy || parsedBirthday === undefined}
          onPress={() => void save()}
        >
          {member ? settingsText.save : settingsText.add}
        </Button>
      </div>
    </div>
  );
}

const EMOJIS = [
  '🦖',
  '🦄',
  '🐱',
  '🐶',
  '🐰',
  '🦊',
  '🐼',
  '🐨',
  '🦁',
  '🐸',
  '🐵',
  '🐧',
  '🦉',
  '🐢',
  '🐬',
  '🦋',
  '🐝',
  '🌻',
  '🌈',
  '⭐',
  '🚀',
  '⚽',
  '🎸',
  '☕',
];

/** The last full emoji typed (flags, skin tones and family emojis are several code points). */
function lastGrapheme(v: string): string {
  const parts = [...new Intl.Segmenter('de', { granularity: 'grapheme' }).segment(v.trim())];
  const last = parts.at(-1)?.segment ?? '';
  return last.length <= 8 ? last : ''; // stored emojis are at most 8 UTF-16 units
}

/** Tap an emoji – laptops have no emoji keyboard; any other one can still be typed. */
function EmojiPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const choices = [
    { value: '', label: settingsText.letter },
    ...EMOJIS.map((e) => ({ value: e, label: e })),
  ];
  return (
    <div className="flex flex-col gap-gap-sm">
      <ChoiceGroup
        label={settingsText.emoji}
        description={settingsText.emojiHint}
        value={EMOJIS.includes(value) || !value ? value : 'other'}
        onChange={(v) => v !== 'other' && onChange(v)}
        choices={
          EMOJIS.includes(value) || !value
            ? choices
            : [...choices, { value: 'other', label: value }]
        }
      />
      <TextField
        label={settingsText.otherEmoji}
        value={EMOJIS.includes(value) ? '' : value}
        onChange={(v) => onChange(lastGrapheme(v))}
      />
    </div>
  );
}
